/**
 * 语言类型统一定义
 *
 * 作用: 避免在多个文件中重复定义相同的语言类型
 *
 * 使用场景:
 * - i18n 配置
 * - 图片资源加载
 * - 音频资源加载
 * - 组件 props 类型
 */

/**
 * 支持的语言列表
 */
export const SUPPORTED_LOCALES = ['zh', 'en', 'ko', 'vi'] as const

/**
 * 支持的语言类型
 *
 * @example
 * ```typescript
 * import type { SupportedLocale } from '@/types/locale'
 *
 * const locale: SupportedLocale = 'zh'
 * ```
 */
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

/**
 * 语言代码映射
 * 将完整的语言代码映射到简化的资源目录名
 *
 * @example
 * ```typescript
 * LOCALE_MAP['zh-CN'] // => 'zh'
 * LOCALE_MAP['en-US'] // => 'en'
 * ```
 */
export const LOCALE_MAP: Record<string, SupportedLocale> = {
  zh: 'zh',
  'zh-CN': 'zh',
  'zh-Hans': 'zh',
  'zh-Hans-CN': 'zh',
  en: 'en',
  'en-US': 'en',
  ko: 'ko',
  'ko-KR': 'ko',
  vi: 'vi',
  'vi-VN': 'vi',
}

/**
 * 将任意语言代码映射到支持的语言
 *
 * @param locale - 语言代码 (可能是完整的,如 'zh-CN')
 * @returns 映射后的语言代码 (简化的,如 'zh')
 *
 * @example
 * ```typescript
 * mapLocale('zh-CN') // => 'zh'
 * mapLocale('en-US') // => 'en'
 * mapLocale('unknown') // => 'zh' (默认中文)
 * ```
 */
export function mapLocale(locale: string): SupportedLocale {
  return LOCALE_MAP[locale] || 'zh' // 默认中文
}

/**
 * 检查是否为支持的语言
 *
 * @param locale - 语言代码
 * @returns 是否支持该语言
 */
export function isSupportedLocale(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale)
}
